import {
  ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS,
  EstablishedAhpSessionResumptionTimeoutError,
  type EstablishedAhpResumptionAttempt,
  type EstablishedAhpResumptionPhase,
} from './contracts'
import type {PageActivity} from '@github-ui/ahp-relay/page-activity'

function abortReason(signal: AbortSignal): Error {
  return signal.reason instanceof Error
    ? signal.reason
    : new EstablishedAhpSessionResumptionTimeoutError(ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS)
}

export function createEstablishedAhpResumptionDeadline(
  timeoutMs = ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS,
  options?: {activity?: PageActivity},
): EstablishedAhpResumptionAttempt {
  const activity = options?.activity
  const controller = new AbortController()
  let phase: EstablishedAhpResumptionPhase = 'initial'
  let expiresAt = Date.now() + timeoutMs
  let activeElapsedMs = 0
  let activeSince = activity?.isActive() ? Date.now() : undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  let disposed = false
  let unsubscribeActivity: (() => void) | undefined
  const remainingMs = () =>
    Math.max(
      0,
      activity
        ? timeoutMs - (activeElapsedMs + (activeSince !== undefined ? Date.now() - activeSince : 0))
        : expiresAt - Date.now(),
    )
  const clearTimer = () => {
    if (timer) clearTimeout(timer)
    timer = undefined
  }
  const cleanup = () => {
    clearTimer()
    unsubscribeActivity?.()
    unsubscribeActivity = undefined
  }
  const onActivityChange = () => {
    if (disposed || controller.signal.aborted || !activity) return
    if (!activity.isActive()) {
      if (activeSince !== undefined) activeElapsedMs += Date.now() - activeSince
      activeSince = undefined
      clearTimer()
    } else if (activeSince === undefined) {
      activeSince = Date.now()
      arm()
    }
  }
  const arm = () => {
    if (disposed || controller.signal.aborted) return
    if (activity && !unsubscribeActivity) unsubscribeActivity = activity.subscribe(onActivityChange)
    if (activity && !activity.isActive()) {
      onActivityChange()
      return
    }
    const remaining = remainingMs()
    if (remaining === 0) {
      if (timer) clearTimeout(timer)
      timer = undefined
      controller.abort(new EstablishedAhpSessionResumptionTimeoutError(timeoutMs))
      return
    }
    if (timer) return
    timer = setTimeout(() => {
      if (!activity) {
        controller.abort(new EstablishedAhpSessionResumptionTimeoutError(timeoutMs))
        return
      }
      timer = undefined
      if (!activity.isActive()) {
        onActivityChange()
        return
      }
      if (remainingMs() > 0) {
        arm()
        return
      }
      controller.abort(new EstablishedAhpSessionResumptionTimeoutError(timeoutMs))
    }, remaining)
  }
  const rearm = () => {
    if (!timer) return
    clearTimeout(timer)
    timer = undefined
    arm()
  }
  const phaseRank: Record<EstablishedAhpResumptionPhase, number> = {
    initial: 0,
    waking: 1,
    connecting: 2,
  }
  if (activity) controller.signal.addEventListener('abort', cleanup, {once: true})

  return {
    get signal() {
      arm()
      return controller.signal
    },
    get phase() {
      return phase
    },
    timeoutMs,
    get expiresAt() {
      return activity ? Date.now() + remainingMs() : expiresAt
    },
    get isDisposed() {
      return disposed
    },
    remainingMs,
    advanceTo: nextPhase => {
      arm()
      if (controller.signal.aborted) throw abortReason(controller.signal)
      if (phaseRank[nextPhase] <= phaseRank[phase]) return
      phase = nextPhase
      expiresAt = Date.now() + timeoutMs
      if (activity) {
        activeElapsedMs = 0
        activeSince = activity.isActive() ? Date.now() : undefined
        clearTimer()
        arm()
      } else {
        rearm()
      }
    },
    waitFor: async work => {
      arm()
      if (controller.signal.aborted) return Promise.reject(abortReason(controller.signal))

      let onAbort = () => {}
      const aborted = new Promise<never>((_resolve, reject) => {
        onAbort = () => reject(abortReason(controller.signal))
        controller.signal.addEventListener('abort', onAbort, {once: true})
      })
      try {
        const result = await Promise.race([work, aborted])
        if (remainingMs() === 0) {
          if (!controller.signal.aborted) {
            controller.abort(new EstablishedAhpSessionResumptionTimeoutError(timeoutMs))
          }
          throw abortReason(controller.signal)
        }
        return result
      } finally {
        controller.signal.removeEventListener('abort', onAbort)
      }
    },
    cancel: reason => {
      cleanup()
      controller.abort(reason ?? new DOMException('The resumption attempt was cancelled.', 'AbortError'))
    },
    dispose: () => {
      disposed = true
      cleanup()
    },
  }
}
